import {
  type ColorCrosswalkField,
  type ColorCrosswalkModifier,
} from "./colorCrosswalk.js";
import { normalizeFinishCode } from "./finishCode.js";

const COLOR_FIELDS = ["roof_color", "siding_color", "trim_color"] as const;
const OPTIONAL_FIELDS = ["siding_type", "roof_type", "roof_style", "package"] as const;

export type ColorCrosswalkColorChoice = {
  field: (typeof COLOR_FIELDS)[number];
  value: string;
};

export type ColorCrosswalkGapInput = {
  unit_id: string;
  material_use_id: string;
  use_code: string;
  finish_channel: string | null;
  aspects: ReadonlyArray<{ field: string; value: string }>;
};

export type ColorCrosswalkCard = {
  key: string;
  finish_channel: string;
  use_family: string;
  source_field: ColorCrosswalkField;
  source_value: string;
  color_choices: ColorCrosswalkColorChoice[];
  modifiers: ColorCrosswalkModifier[];
  optional_modifiers: ColorCrosswalkModifier[];
  use_codes: string[];
  material_use_ids: string[];
  serial_count: number;
  gap_count: number;
};

/** Length suffixes stay off the family, so panel 36 and panel 70 map together. */
export function partUseFamily(useCode: string): string {
  return useCode.trim().replace(/(?:_\d+)+$/, "");
}

function aspectValue(aspects: ColorCrosswalkGapInput["aspects"], field: string): string {
  return aspects.find((row) => row.field === field)?.value.trim() ?? "";
}

/** Which order tag carries the color for this part-use channel. */
export function defaultColorSourceField(
  channel: string,
  aspects: ReadonlyArray<{ field: string }>,
): ColorCrosswalkField | null {
  const norm = normalizeFinishCode(channel);
  const preferred: ColorCrosswalkField | null =
    norm === "roof" || norm === "roofing" || norm.endsWith("_roof") || norm.endsWith("_roofing")
      ? "roof_color"
      : norm === "trim" || norm.endsWith("_trim")
        ? "trim_color"
        : norm === "paint" || norm === "siding" || norm.endsWith("_paint") || norm.endsWith("_siding")
          ? "siding_color"
          : null;
  const present = new Set(aspects.map((row) => row.field));
  if (preferred) return present.has(preferred) ? preferred : null;
  const colors = COLOR_FIELDS.filter((field) => present.has(field));
  if (colors.length === 1) return colors[0]!;
  return null;
}

type Bucket = {
  channel: string;
  family: string;
  series: string;
  sourceField: ColorCrosswalkColorChoice["field"];
  sourceValue: string;
  gaps: ColorCrosswalkGapInput[];
};

/**
 * One card per part-use family and one color statement.
 * Panel lengths stay together. Rakes, ridge, and screws do not share that card.
 */
export function proposeColorCrosswalkCards(gaps: readonly ColorCrosswalkGapInput[]): ColorCrosswalkCard[] {
  const buckets = new Map<string, Bucket>();
  for (const gap of gaps) {
    const channel = gap.finish_channel?.trim() ?? "";
    if (!channel) continue;
    const preferred = defaultColorSourceField(channel, gap.aspects);
    const fields: Array<(typeof COLOR_FIELDS)[number]> =
      preferred && (COLOR_FIELDS as readonly string[]).includes(preferred)
        ? [preferred as (typeof COLOR_FIELDS)[number]]
        : preferred
          ? []
          : COLOR_FIELDS.filter((field) => aspectValue(gap.aspects, field));
    const series = aspectValue(gap.aspects, "series");
    const family = partUseFamily(gap.use_code);
    for (const field of fields) {
      const sourceValue = aspectValue(gap.aspects, field);
      if (!sourceValue) continue;
      const key = [
        normalizeFinishCode(channel),
        family,
        field,
        normalizeFinishCode(sourceValue),
        normalizeFinishCode(series),
      ].join("|");
      const bucket: Bucket = buckets.get(key) ?? { channel, family, series, sourceField: field, sourceValue, gaps: [] };
      bucket.gaps.push(gap);
      buckets.set(key, bucket);
    }
  }

  return [...buckets.values()]
    .map((bucket) => toCard(bucket))
    .filter((card): card is ColorCrosswalkCard => card !== null)
    .sort((a, b) => b.serial_count - a.serial_count || a.key.localeCompare(b.key));
}

function toCard(bucket: Bucket): ColorCrosswalkCard | null {
  const choices: ColorCrosswalkColorChoice[] = [{ field: bucket.sourceField, value: bucket.sourceValue }];
  const modifiers: ColorCrosswalkModifier[] = bucket.series
    ? [{ field: "series", op: "equals", value: bucket.series }]
    : [];
  const optional = new Map<string, ColorCrosswalkModifier>();
  const addonCounts = new Map<string, number>();
  for (const gap of bucket.gaps) {
    for (const field of OPTIONAL_FIELDS) {
      const value = aspectValue(gap.aspects, field);
      if (!value) continue;
      optional.set(`${field}:${value}`, { field, op: "equals", value });
    }
    for (const row of gap.aspects) {
      if (row.field !== "addon_name" || !row.value.trim()) continue;
      const name = row.value.trim();
      addonCounts.set(name, (addonCounts.get(name) ?? 0) + 1);
    }
  }
  const addons = [...addonCounts.entries()]
    .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
    .slice(0, 6)
    .map(([name]) => ({ field: "addon_name" as const, op: "contains" as const, value: name }));

  const useCodes = [...new Set(bucket.gaps.map((gap) => gap.use_code))].sort();
  const useIds = [...new Set(bucket.gaps.map((gap) => gap.material_use_id))].sort();
  const serials = new Set(bucket.gaps.map((gap) => gap.unit_id));
  const key = [
    normalizeFinishCode(bucket.channel),
    bucket.family,
    bucket.sourceField,
    normalizeFinishCode(bucket.sourceValue),
    normalizeFinishCode(bucket.series),
  ].join("|");
  return {
    key,
    finish_channel: normalizeFinishCode(bucket.channel),
    use_family: bucket.family,
    source_field: bucket.sourceField,
    source_value: bucket.sourceValue,
    color_choices: choices,
    modifiers,
    optional_modifiers: [...optional.values(), ...addons],
    use_codes: useCodes,
    material_use_ids: useIds,
    serial_count: serials.size,
    gap_count: bucket.gaps.length,
  };
}
